import { Controller } from "@hotwired/stimulus"
import * as d3 from 'd3'
import bb from 'billboard.js/dist/billboard.js'
import merge from 'deepmerge'
import { useIntersection, useWindowResize, useDebounce } from 'stimulus-use'
import { Formatter } from "../utils/formatter"
import { ChartConfig } from '../charts/chart_config'

// Connects to data-controller="graph"
export default class extends Controller {
  static targets = ['container', 'loadbar']
  static RESIZE_SETTLE_MS = 180

  static values = {
    bindId: String,
    loadEvent: String,
    announce: String,
    config: Object,
    data: Array
  }

  static debounces = ['debouncedResize']

  connect() {
    if (this.isDragging) {
      return
    }

    useWindowResize(this)
    useDebounce(this)

    this.resizeSettleTimer = null
    this.initialResizeTimer = null
    this.resizeObserver = null
    this.handleUiExpanded = () => this.resize()
    this.handleUiCollapsed = () => this.resize()

    app.graphs ||= {}
    this.name = this.bindId?.replace('#', '')
    this.bbLoaded = false

    this.initConfigHandler()
    this.initForPageContext()
    this.startResizeObserver()

    // is the chart hidden? load when visible
    // handle delayed loading (?)
    if (this.shouldLoadImmediately) {
      this.load()
    } else {
      document.addEventListener(this.loadEvent, this.load, { once: true })
    }

    document.addEventListener("ui:expanded", this.handleUiExpanded)
    document.addEventListener("ui:collapsed", this.handleUiCollapsed)
  }

  disconnect() {
    if (this.isDragging) {
      return
    }

    document.removeEventListener("ui:expanded", this.handleUiExpanded)
    document.removeEventListener("ui:collapsed", this.handleUiCollapsed)
    this.clearResizeSettleTimer()
    this.clearInitialResizeTimer()
    this.stopResizeObserver()

    this.unregister()
  }

  // Stim-use Callbacks
  // ------------------------------------------------------------------------ //

  // useIntersection callbacks
  appear(entry) {
    this.chart?.flush()
    this.bbLoaded = true
    this.resize()
  }

  disappear(entry) {}

  // useWindowResize
  windowResize({ width, height, event }) {
    this.debouncedResize()
    this.scheduleFinalResize()
  }

  // Config Handling
  // ------------------------------------------------------------------------ //

  initConfigHandler() {
    this.config = new ChartConfig(
      merge(this.configValue, { bindto: this.bindId }),
      this.dataValue
    )
  }

  get finalConfig() {
    // temporary hack for charts with column data
    if (this.configValue.data?.columns) {
      let cfg = this.config.finalConfig
      delete cfg.data.json
      return cfg
    } else {
      return merge(this.config.finalConfig, { data: { json: this.dataValue } })
    }
  }

  // Chart instance management
  // ------------------------------------------------------------------------ //

  load() {
    if (this.shouldLoad) {
      this.generate()
      this.register()
      this.scheduleInitialResize()

      this.announceLoaded()
    }
  }

  generate() {
    this.chart = bb.generate(this.finalConfig)
  }

  announceLoaded() {
    this.dispatch('loaded', { detail: { graph: this } })
  }

  // Should only destroy if the BB chart was actually generated
  destroy() {
    if (this.chart && this.bbLoaded) {
      this.chart.destroy()
    }
  }

  change(data) {
    this.changeData = data
    this.rebuild()
  }

  rebuild() {
    this.destroy()
    this.generate()
  }

  register() {
    app.charts.push(this.chart)
    app.graphs[this.name] = this
  }

  unregister() {
    if (Boolean(this.chart)) {
      app.charts?.splice(app.charts.indexOf(this.chart), 1)
      this.destroy()
    }

    if (Boolean(app?.graphs[this.name])) {
      delete app.graphs[this.name]
    }
  }

  initForPageContext() {
    this.tabPanel = this.element.closest('[data-tabs-target="panel"]')

    if (this.isInTabPanel) {
      this.config.renderLazy()
      useIntersection(this, { element: this.tabPanel, eventPrefix: this.bindId })
    } else {
      useIntersection(this, { eventPrefix: this.bindId })
    }
  }

  // change chart type or normalize
  // convert to normalized

  get loadEvent() {
    if (this.hasLoadEventValue) {
      return this.loadEventValue
    } else {
      return 'immediate'
    }
  }

  get isInTabPanel() {
    return Boolean(this.tabPanel)
  }

  get shouldLoad() {
    return !(document.documentElement.hasAttribute("data-turbo-preview"))
  }

  get shouldLoadImmediately() {
    return !this.hasLoadEventValue
  }

  // Check if the chart is inside a sortable element being dragged. When Sortable
  // moves an element, it triggers mutations that disconnect and reconnect the
  // controller for each swap.
  get isDragging() {
    return this.element.closest('.sortable-dragging')
  }

  // Data
  // ------------------------------------------------------------------------ //

  // Used with app.dataLookup
  get currentData() {
    return { data: { json: this.dataValue } }
  }

  get chartData() {
    return this.dataValue || this.configValue?.data?.json || this.configValue?.data?.columns
  }

  get insufficientData() {
    return !Boolean(this.chartData) || !Boolean(this.chartData.length)
  }

  // Find the chart container element
  get bindElement() {
    return document.querySelector(this.bindId)
  }

  get bindId() {
    if (this.hasBindIdValue) {
      return `#${this.bindIdValue}`
    } else {
      return `#${this.element.id}`
    }
  }

  // Resizing
  // ------------------------------------------------------------------------ //

  scheduleInitialResize() {
    this.clearInitialResizeTimer()

    // Run after initial paint/layout and once more after layout settles.
    requestAnimationFrame(() => requestAnimationFrame(() => this.resize()))
    this.initialResizeTimer = window.setTimeout(() => {
      this.initialResizeTimer = null
      this.resize()
    }, this.constructor.RESIZE_SETTLE_MS)
  }

  clearInitialResizeTimer() {
    if (this.initialResizeTimer) {
      window.clearTimeout(this.initialResizeTimer)
      this.initialResizeTimer = null
    }
  }

  scheduleFinalResize() {
    this.clearResizeSettleTimer()

    // Guarantee one trailing resize after the resize stream settles.
    this.resizeSettleTimer = window.setTimeout(() => {
      this.resizeSettleTimer = null
      this.resize()
    }, this.constructor.RESIZE_SETTLE_MS)
  }

  clearResizeSettleTimer() {
    if (this.resizeSettleTimer) {
      window.clearTimeout(this.resizeSettleTimer)
      this.resizeSettleTimer = null
    }
  }

  startResizeObserver() {
    if (!window.ResizeObserver || this.resizeObserver) {
      return
    }

    this.resizeObserver = new ResizeObserver(() => {
      // Ignore observations triggered by the chart's own resize call.
      if (this._resizing) { return }
      this.debouncedResize()
      this.scheduleFinalResize()
    })

    // Observe the nearest stable layout ancestor, not the chart element itself.
    // Observing the chart element causes a feedback loop: resize changes chart
    // dimensions → observer fires → resize → repeat.
    const layoutAncestor = this.element.closest('[id="app_main"], .page-main, .page-content, .printable-content, .app-content') || this.element.parentElement
    this.resizeObserver.observe(layoutAncestor)
  }

  stopResizeObserver() {
    this.resizeObserver?.disconnect()
    this.resizeObserver = null
  }

  resize() {
    if (!this.chart || this._resizing) {
      return
    }

    // Guard against re-entrant calls (e.g. ResizeObserver firing on our own resize).
    // Cleared after one frame so the next legitimate resize can proceed.
    this._resizing = true
    requestAnimationFrame(() => { this._resizing = false })

    // Let Billboard measure its container rather than computing an explicit
    // width here. Computing DOM width and passing it back creates a feedback
    // loop when the chart element is a flex item: Billboard writes SVG width →
    // the flex item shrinks to match → we measure the shrunken value → we pass
    // it back to Billboard → chart shrinks further → death spiral.
    // Our ResizeObserver and trailing timer ensure this is called at the right
    // moment, so Billboard's own container measurement is reliable.
    this.chart.resize()
  }

  debouncedResize() { this.resize() }
}
